import React from 'react';
import {
  ChefHat, MessageCircle, ArrowUp, ArrowDown, Minus,
  CheckCircle2, AlertTriangle, AlertCircle, Ban,
} from 'lucide-react';

// One-row client snapshot: plan / chat / weight-vs-goal context plus the two next actions.
// Spec: DESIGN.md (borders only, tabular numbers, pills = icon + word + color).

const COPY = {
  english: {
    heading: 'Client snapshot',
    mealPlans: 'Meal plans',
    chats: 'Chats',
    weight: 'Weight',
    since: 'since',
    oneWeighIn: '1 weigh-in',
    active: 'active',
    total: 'total',
    noPlanYet: 'No plan yet',
    noActivePlan: 'No active plan',
    notStarted: 'Not started',
    noWeighIns: 'No weigh-ins yet',
    onPlan: 'On plan',
    holding: 'Holding',
    offPlan: 'Off plan',
    kg: 'kg',
    createPlan: 'Create meal plan',
    openChat: 'Open chat',
    startChat: 'Start chat',
    srUp: 'Up',
    srDown: 'Down',
    srFlat: 'No change',
    srKg: 'kilograms',
  },
  hebrew: {
    heading: 'תמונת מצב',
    mealPlans: 'תפריטים',
    chats: 'שיחות',
    weight: 'משקל',
    since: 'מאז',
    oneWeighIn: 'שקילה אחת',
    active: 'פעילים',
    total: 'סה"כ',
    noPlanYet: 'אין תפריט עדיין',
    noActivePlan: 'אין תפריט פעיל',
    notStarted: 'לא התחיל',
    noWeighIns: 'אין שקילות עדיין',
    onPlan: 'בתוכנית',
    holding: 'יציב',
    offPlan: 'סוטה מהתוכנית',
    kg: 'ק"ג',
    createPlan: 'צור תפריט',
    openChat: "פתח צ'אט",
    startChat: "התחל צ'אט",
    srUp: 'עלייה של',
    srDown: 'ירידה של',
    srFlat: 'ללא שינוי',
    srKg: 'ק"ג',
  },
};

const PILL = 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap';
const PILL_TONE = {
  forest: 'bg-forest-faint text-forest-ondark',
  neutral: 'bg-moss text-ink',
  danger: 'bg-red-700/10 text-red-700 dark:bg-red-400/15 dark:text-red-400',
  warning: 'bg-amber-700/10 text-amber-700 dark:bg-amber-400/15 dark:text-amber-400',
};

const BTN = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 text-[13px] font-semibold whitespace-nowrap transition-colors duration-120 ease-out active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

function Pill({ tone, icon: Icon, children }) {
  return (
    <span className={`${PILL} ${PILL_TONE[tone]}`}>
      <Icon size={12} aria-hidden="true" />
      {children}
    </span>
  );
}

function StatCell({ label, children, first }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 px-3 py-3 sm:px-4 ${first ? '' : 'border-s border-line'}`}>
      <dt className="truncate text-[11px] font-medium leading-4 text-ink-muted">{label}</dt>
      <dd className="flex flex-wrap items-center gap-x-2 gap-y-1">{children}</dd>
    </div>
  );
}

// ±0.5 kg is noise. Health/performance goals (or none) get no verdict: a wrong "Off plan" is worse than none.
export function weightVerdict(goal, delta) {
  const g = (goal || '').toLowerCase();
  if (g === 'lose') return delta <= -0.5 ? 'onPlan' : delta >= 0.5 ? 'offPlan' : 'holding';
  if (g === 'gain' || g === 'muscle') return delta >= 0.5 ? 'onPlan' : delta <= -0.5 ? 'offPlan' : 'holding';
  if (g === 'maintain') return Math.abs(delta) <= 1 ? 'onPlan' : 'offPlan';
  return null;
}

const VERDICT_PILL = {
  onPlan: { tone: 'forest', icon: CheckCircle2 },
  holding: { tone: 'neutral', icon: Minus },
  offPlan: { tone: 'danger', icon: AlertTriangle },
};

export default function ClientSnapshotBar({ stats, weightLogs, goal, language, onCreatePlan, onOpenChat }) {
  const c = COPY[language] || COPY.english;
  const locale = language === 'hebrew' ? 'he-IL' : 'en-GB';
  const num = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const totalMenus = stats.totalMenus || 0;
  const activeMenus = stats.activeMenus || 0;
  const chatSessions = stats.chatSessions || 0;

  const logs = [...(weightLogs || [])]
    .filter((l) => l && l.weight_kg != null && l.measurement_date)
    .sort((a, b) => new Date(a.measurement_date) - new Date(b.measurement_date));
  const first = logs[0];
  const latest = logs[logs.length - 1];
  const delta = logs.length >= 2 ? Number(latest.weight_kg) - Number(first.weight_kg) : null;
  const flat = delta != null && Math.abs(delta) < 0.05;
  const verdict = delta != null ? weightVerdict(goal, delta) : null;
  const sinceDate = first ? new Date(first.measurement_date).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '';

  let weightLabel = c.weight;
  if (logs.length >= 2) weightLabel = `${c.weight} · ${c.since} ${sinceDate}`;
  else if (logs.length === 1) weightLabel = `${c.weight} · ${c.oneWeighIn}`;

  const signed = (d) => (flat ? num.format(0) : `${d > 0 ? '+' : '−'}${num.format(Math.abs(d))}`);
  const DeltaIcon = flat ? Minus : delta > 0 ? ArrowUp : ArrowDown;

  return (
    <section
      data-tour="playbook-client-snapshot"
      aria-labelledby="client-snapshot-heading"
      className="mb-8 flex flex-col rounded-lg border border-line bg-surface lg:flex-row lg:items-stretch"
    >
      <h2 id="client-snapshot-heading" className="sr-only">{c.heading}</h2>

      <dl className="grid min-w-0 flex-1 grid-cols-3">
        <StatCell label={c.mealPlans} first>
          {activeMenus >= 1 ? (
            <>
              <span className="text-lg font-semibold leading-6 tabular-nums text-ink">
                <bdi dir="ltr">{activeMenus}</bdi> <span className="text-[13px] font-medium">{c.active}</span>
              </span>
              <span className="text-[13px] tabular-nums text-ink-muted">/<bdi dir="ltr">{totalMenus}</bdi> {c.total}</span>
            </>
          ) : (
            <>
              <Pill tone="danger" icon={Ban}>{totalMenus > 0 ? c.noActivePlan : c.noPlanYet}</Pill>
              <span className="text-[13px] tabular-nums text-ink-muted"><bdi dir="ltr">{totalMenus}</bdi> {c.total}</span>
            </>
          )}
        </StatCell>

        <StatCell label={c.chats}>
          <span className="text-lg font-semibold leading-6 tabular-nums text-ink"><bdi dir="ltr">{chatSessions}</bdi></span>
          {chatSessions === 0 && <Pill tone="warning" icon={AlertCircle}>{c.notStarted}</Pill>}
        </StatCell>

        <StatCell label={weightLabel}>
          {logs.length === 0 && <span className="text-[13px] text-ink-muted">{c.noWeighIns}</span>}
          {logs.length === 1 && (
            <span className="text-lg font-semibold leading-6 tabular-nums text-ink">
              <bdi dir="ltr">{num.format(Number(latest.weight_kg))} {c.kg}</bdi>
            </span>
          )}
          {delta != null && (
            <>
              <span className="inline-flex items-center gap-1 text-lg font-semibold leading-6 tabular-nums text-ink">
                <DeltaIcon size={14} className="text-ink-muted" aria-hidden="true" />
                <bdi dir="ltr" aria-hidden="true">{signed(delta)} {c.kg}</bdi>
                <span className="sr-only">
                  {flat ? c.srFlat : `${delta > 0 ? c.srUp : c.srDown} ${num.format(Math.abs(delta))} ${c.srKg}`}, {c.since} {sinceDate}
                </span>
              </span>
              {verdict && (
                <Pill tone={VERDICT_PILL[verdict].tone} icon={VERDICT_PILL[verdict].icon}>{c[verdict]}</Pill>
              )}
            </>
          )}
        </StatCell>
      </dl>

      <div
        data-tour="playbook-quick-actions"
        className="grid grid-cols-2 gap-2 border-t border-line p-3 lg:flex lg:shrink-0 lg:items-center lg:border-s lg:border-t-0 lg:px-4"
      >
        <button
          type="button"
          onClick={onCreatePlan}
          className={`${BTN} bg-forest-ondark text-white hover:bg-forest-ondark/90 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover`}
        >
          <ChefHat size={16} aria-hidden="true" />
          {c.createPlan}
        </button>
        <button
          type="button"
          data-tour="playbook-go-chat"
          onClick={onOpenChat}
          className={`${BTN} border border-line bg-transparent text-ink hover:bg-moss`}
        >
          <MessageCircle size={16} aria-hidden="true" />
          {chatSessions >= 1 ? c.openChat : c.startChat}
        </button>
      </div>
    </section>
  );
}
